import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { message } from '@myua/kit';
import { Action } from 'redux';
import * as api from '../../api';
import { Asset, AssetsData } from '../../api';
import { RootSelector } from '../../reducers';
import * as favoriteActions from '../favorite/actions';
import * as lastStateActions from '../lastState/actions';
import * as packActions from '../pack/actions';
import { getPage } from '../router/selectors';
import * as sharingActions from '../sharing/actions';
import { getSharingId } from '../sharing/selectors';
import * as tagBinderActions from '../tagBinder/actions';
import { ViewMode } from '../viewMode/model';
import { getViewMode } from '../viewMode/selectors';
import * as viewerActions from '../viewer/actions';
import { ViewerShape } from '../viewer/model';
import * as finderActions from './actions';
import { AssetsCache, CacheEntry, Loading, LoadingMode, Mode, Page, newScrollHash } from './model';
import {
  getAssetsComposition,
  getData,
  getDetails,
  getDetailsLoading,
  getDetailsVisible,
  getMode,
  getSelected,
} from './selectors';
import { ListenerApi, takeLatest, takeEvery, takeOnce } from '../../listenerMiddleware';
import * as appActions from '../app/actions';
import { isCacheStale } from '../../cache';

export const finderListeners = () => {
  watchAssetClicked();
  watchRefreshDetailsRequested();
  watchNavigationKeyPressed();
  watchNextPageRequested();
  watchLoadAssetsRequested();
  watchLastState();
  requestDetails();
};

function selectRange(
  id1: Asset['id'],
  id2: Asset['id'],
  { getState }: Pick<ListenerApi, 'getState'>,
) {
  // Выбираем ассеты с id1 и id2, а также все, расположенные между ними.
  // При этом неважно, какой из ассетов расположен раньше - с id1 или id2.
  // Возвращаем id всех выбранных ассетов.

  let result: Asset['id'][] = [];
  const data = getData(getState());
  const index1 = data.assets.findIndex(({ id }) => id === id1);
  const index2 = data.assets.findIndex(({ id }) => id === id2);
  if (index1 !== -1 && index2 !== -1) {
    const [from, to] = index1 < index2 ? [index1, index2] : [index2, index1];
    result = data.assets.slice(from, to + 1).map(({ id }) => id);
  }
  return result;
}

function watchAssetClicked() {
  takeLatest(finderActions.assetClicked, ({ payload }, { getState, dispatch }) => {
    const selected = getSelected(getState());

    // Сайдбар деталей НЕ раскрывается автоматически при выборе ассета: если юзер
    // свернул его (крестиком или кнопкой-тоглом), он остаётся свёрнутым до тех пор,
    // пока его не откроют вручную кнопкой в шапке (MYUA-16254) или повторным кликом
    // по уже выбранному ассету (см. ниже).

    // Выделение сделано аналогично Windows (в masOS оно сложнее).

    if (payload.ctrlKey) {
      // Если кликнули по выделенному ассету, то снимаем с него выделение, иначе выделяем его.
      // В обоих случаях маркерами начала и конца выделения будет кликнутый ассет.
      const { id } = payload;
      if (selected.ids.includes(id)) {
        dispatch(
          finderActions.assetSelected({
            ids: selected.ids.filter((selectedId) => selectedId !== id),
            firstId: id,
            lastId: id,
          }),
        );
      } else {
        dispatch(
          finderActions.assetSelected({ ids: [...selected.ids, id], firstId: id, lastId: id }),
        );
      }
    } else if (payload.shiftKey) {
      // Если был зажат шифт, то начало выделения остается прежним (если оно было).
      // Концом выделения является кликнутый ассет.
      // Если за пределами нового диапазона остались какие-то другие отдельные выделения (сделанные через ctrl),
      // то они исчезают.
      const firstId = selected.firstId || payload.id;
      const lastId = payload.id;
      const ids = selectRange(firstId, lastId, { getState });
      dispatch(finderActions.assetSelected({ ids, firstId, lastId }));
    } else {
      // Если кликнули по единственному выделенному ассету, то возвращаем свёрнутый
      // сайдбар деталей (второй способ его открыть, помимо кнопки в шапке),
      // иначе выделяем единственный ассет.
      const { id } = payload;
      const alreadySelected = selected.ids.length === 1 && selected.ids[0] === id;
      if (alreadySelected) {
        if (!getDetailsVisible(getState())) {
          dispatch(finderActions.setDetailsVisible({ visible: true }));
        }
      } else {
        dispatch(finderActions.assetSelected({ ids: [id], firstId: id, lastId: id }));
      }
    }
  });
}

function requestDetails() {
  takeOnce(appActions.initialized, (action, { dispatch }) => {
    dispatch(finderActions.detailsRequested());
  });

  takeEvery(
    [finderActions.assetSelected, tagBinderActions.completed, lastStateActions.applied],
    (action, { dispatch }) => {
      dispatch(finderActions.detailsRequested());
    },
  );
}

function watchRefreshDetailsRequested() {
  takeLatest(finderActions.detailsRequested, async (action, { getState, dispatch, signal }) => {
    const selected = getSelected(getState());
    const viewMode = getViewMode(getState());

    // Если выбрано несколько ассетов или не выбран ни один, то details не имеют смысла.
    // Так же details не имеют смысла в режиме просмотра креативов.
    // Сбрасываем details и больше ничего не делаем.

    if (selected.ids.length !== 1 || viewMode === ViewMode.creatives) {
      const details = getDetails(getState());
      if (details.asset !== undefined || details.error !== false) {
        dispatch(finderActions.detailsChanged({ asset: undefined, error: false }));
      }
      const loading = getDetailsLoading(getState());
      if (loading) {
        dispatch(finderActions.setDetailsLoading({ loading: false }));
      }
      return;
    }

    // Если же выбран один ассет, то загружаем details для него.

    const page = getPage(getState());
    const sharingId = getSharingId(getState());

    dispatch(finderActions.setDetailsLoading({ loading: true }));
    const result = await api.loadAssetDetails(
      selected.ids[0],
      page === Page.sharing ? sharingId : undefined,
    );
    if (signal.aborted) {
      return;
    }
    if (result.success) {
      dispatch(finderActions.detailsChanged({ asset: result.data.response, error: false }));
    } else {
      const status = result.errorData?.status!;
      dispatch(
        finderActions.detailsChanged({
          asset: undefined,
          error: status,
        }),
      );
    }

    dispatch(finderActions.setDetailsLoading({ loading: false }));
  });
}

function watchNavigationKeyPressed() {
  takeLatest(finderActions.navigationKeyPressed, ({ payload }, { getState, dispatch }) => {
    // Если клавиша нажата, когда ни один ассет не выбран, то ничего не делаем
    const selected = getSelected(getState());
    if (selected.ids.length === 0) {
      return;
    }

    // Находим ячейку с выбранным позже всех ассетом

    const { rows, assetsPerRow } = getAssetsComposition(getState());

    let cellIndex = 0;
    const rowIndex = rows.findIndex((cells) => {
      cellIndex = cells.findIndex(({ id }) => id === selected.lastId);
      return cellIndex >= 0;
    });

    if (rowIndex < 0 || cellIndex < 0) {
      return;
    }

    // Определяем, какой ассет надо выбрать следующим

    let id: number | undefined;
    const { key, shiftKey, ctrlKey } = payload;

    if (key === 'ArrowLeft') {
      id = rows[rowIndex]?.[cellIndex - 1]?.id ?? rows[rowIndex - 1]?.[assetsPerRow - 1]?.id;
    } else if (key === 'ArrowRight') {
      id = rows[rowIndex]?.[cellIndex + 1]?.id ?? rows[rowIndex + 1]?.[0]?.id;
    } else if (key === 'ArrowUp') {
      id = rows[rowIndex - 1]?.[cellIndex]?.id;
    } else if (key === 'ArrowDown') {
      id = rows[rowIndex + 1]?.[cellIndex]?.id;
    }

    if (id === undefined) {
      return;
    }

    // Выделение сделано аналогично Windows (в masOS оно сложнее).

    if (ctrlKey) {
      // Ничего не делаем.
    } else if (shiftKey) {
      // Если был зажат шифт, то начало выделения остается прежним (если оно было).
      // Концом выделения является новый ассет.
      // Если за пределами нового диапазона остались какие-то другие отдельные выделения (сделанные через ctrl),
      // то они исчезают.
      const firstId = selected.firstId || id;
      const lastId = id;
      const ids = selectRange(firstId, lastId, { getState });
      dispatch(finderActions.assetSelected({ ids, firstId, lastId }));
    } else {
      // Выделяем единственный ассет
      dispatch(finderActions.assetSelected({ ids: [id], firstId: id, lastId: id }));
    }
  });
}

function watchLoadAssetsRequested() {
  takeLatest(finderActions.loadAssetsRequested, ({ payload }, { getState, dispatch }) => {
    const mode = getMode(getState());
    if (!mode) {
      return;
    }

    // force имеет смысл только там, где есть кэш локации (viewer): у pack/favorite/sharing
    // кэш выключен, они и так всегда идут в сеть.
    const force = payload?.force;

    const source: Record<Mode, Action> = {
      [Page.viewer]: viewerActions.loadAssetsRequested({ mode: LoadingMode.refresh, force }),
      [Page.pack]: packActions.loadPackRequested({ mode: LoadingMode.refresh }),
      [Page.favorite]: favoriteActions.loadFavoritesRequested({ mode: LoadingMode.refresh }),
      [Page.sharing]: sharingActions.loadDataRequested({ mode: LoadingMode.refresh }),
    };
    dispatch(source[mode]);
  });
}

function watchNextPageRequested() {
  takeLatest(finderActions.nextPageRequested, (action, { getState, dispatch }) => {
    const mode = getMode(getState());
    if (!mode) {
      return;
    }

    const source: Record<Mode, Action | null> = {
      [Page.viewer]: viewerActions.loadAssetsRequested({ mode: LoadingMode.nextPage }),
      [Page.pack]: packActions.loadPackRequested({ mode: LoadingMode.nextPage }),
      [Page.favorite]: favoriteActions.loadFavoritesRequested({ mode: LoadingMode.nextPage }),
      [Page.sharing]: sharingActions.loadDataRequested({ mode: LoadingMode.nextPage }),
    };
    const sourceAction = source[mode];
    if (sourceAction) {
      dispatch(sourceAction);
    }
  });
}

function watchLastState() {
  takeLatest(
    [finderActions.setDetailsVisible, finderActions.assetSelected],
    (action, { dispatch }) => {
      dispatch(lastStateActions.saveRequested());
    },
  );
}

export type LoadWithPagination<T extends AssetsData> = {
  mode: LoadingMode;
  getData: RootSelector<ViewerShape['data']>;
  getScrollHash: RootSelector<string>;
  setLoading: (options: { loading: Loading }) => PayloadAction<typeof options>;
  onRequest: (offset: number | undefined) => Promise<api.APIScenarioReturn<T>>;
  assetsChanged: (options: {
    data: T;
    hasNextPage: boolean;
    scrollHash: string;
  }) => PayloadAction<typeof options>;
  errorMessage: string;
  errorState: T;
  // Опциональное кэширование списков по ключу локации (мгновенная навигация).
  // Задаётся только там, где список безопасно кэшировать (viewer); при отсутствии
  // поведение не меняется — каждый заход делает свежий запрос со спиннером.
  cacheKey?: string;
  getHasNextPage?: RootSelector<boolean>;
  getCache?: RootSelector<AssetsCache<ViewerShape['data']>>;
  storeCache?: (options: {
    key: string;
    entry: CacheEntry<ViewerShape['data']>;
  }) => PayloadAction<typeof options>;
  invalidateCache?: (options: { key: string }) => PayloadAction<typeof options>;
  // Данные на сервере изменились по нашей же инициативе (upload/delete) — кэш локации
  // выбрасываем и идём в сеть.
  force?: boolean;
};

export async function loadWithPagination<T extends AssetsData>(
  {
    mode,
    getData,
    getScrollHash,
    setLoading,
    onRequest,
    assetsChanged,
    errorMessage,
    errorState,
    cacheKey,
    getHasNextPage,
    getCache,
    storeCache,
    invalidateCache,
    force,
  }: LoadWithPagination<T>,
  { getState, dispatch, signal }: Pick<ListenerApi, 'getState' | 'dispatch' | 'signal'>,
) {
  const cachingEnabled = Boolean(cacheKey && getCache && getHasNextPage && storeCache);

  let offset: number | undefined;
  let revalidating = false;

  if (mode === LoadingMode.nextPage) {
    const data = getData(getState());
    offset = data.assets.length;
    dispatch(setLoading({ loading: Loading.nextPage }));
  } else if (force) {
    // Перечитывание после нашей же мутации (upload/delete). Кэш локации устарел, причём
    // ключ не изменился — поэтому выбрасываем запись и идём в сеть. Если данные уже на
    // экране, о загрузке сообщает полоса, а не скелетон (см. Assets: скелетон рисуется
    // только при пустом списке).
    if (cachingEnabled && invalidateCache) {
      dispatch(invalidateCache({ key: cacheKey! }));
    }
    dispatch(setLoading({ loading: Loading.refresh }));
  } else {
    // refresh: если для этой локации есть кэш — мгновенно показываем его без спиннера.
    const cached = cachingEnabled ? getCache!(getState())[cacheKey!] : undefined;
    if (cached) {
      dispatch(setLoading({ loading: Loading.none }));
      dispatch(
        assetsChanged({
          data: cached.data as unknown as T,
          hasNextPage: cached.hasNextPage,
          scrollHash: cached.scrollHash,
        }),
      );
      if (!isCacheStale(cached)) {
        // Запись свежая (получена в этой сессии только что) — выходим без сетевого
        // запроса, это аналог staleTime.
        return;
      }
      // Запись либо из прошлой сессии, либо пролежала дольше SESSION_STALE_MS: показали
      // мгновенно, а ниже тихо обновляем в фоне (SWR). Ответ на refresh — это только
      // первая страница, поэтому при мердже ниже хвост подгруженных страниц сохраняем.
      revalidating = true;
    } else {
      dispatch(setLoading({ loading: Loading.refresh }));
    }
  }

  const result = await onRequest(offset);
  if (signal.aborted) {
    return;
  }
  dispatch(setLoading({ loading: Loading.none }));

  if (result.success) {
    const response = result.data.response;

    let assets: Asset[];
    let scrollHash: string;

    if (mode === LoadingMode.nextPage) {
      // Добавляем новые ассеты в конец списка ранее загруженных ассетов.
      // Важно убрать дубликаты из ранее загруженных ассетов, т.к. какой-то из прежних ассетов
      // может прийти заново (если между загрузками страниц данные в базе поменялись).
      const newIds: Partial<Record<Asset['id'], true>> = {};
      response.assets.forEach(({ id }) => {
        newIds[id] = true;
      });
      const data = getData(getState());
      assets = [...data.assets.filter(({ id }) => !newIds[id]), ...response.assets];
      scrollHash = getScrollHash(getState());
    } else if (revalidating) {
      // Тихий ре-валидейт: на экране данные из кэша, возможно с подгруженными страницами,
      // а из сети пришла только первая. Отдать одну страницу значило бы «схлопнуть» список
      // под пользователем, поэтому первую страницу заменяем свежей, а хвост оставляем как
      // есть (он обновится, когда до него доскроллят). Дубликаты убираем: элемент из хвоста
      // мог попасть в новую первую страницу, если данные на сервере сдвинулись.
      const shown = getData(getState()).assets;
      if (shown.length > response.assets.length) {
        const freshIds: Partial<Record<Asset['id'], true>> = {};
        response.assets.forEach(({ id }) => {
          freshIds[id] = true;
        });
        assets = [
          ...response.assets,
          ...shown.slice(response.assets.length).filter(({ id }) => !freshIds[id]),
        ];
      } else {
        assets = response.assets;
      }
      // Новый scrollHash не генерируем — данные уже показаны из кэша, не сбрасываем
      // скролл под пользователем.
      scrollHash = getScrollHash(getState());
    } else {
      assets = response.assets;
      scrollHash = newScrollHash();
    }

    // Раньше следующая страница обещалась по одному признаку «текущая непустая». Из-за
    // этого после последней полной страницы список показывал ряд-заглушку, которая
    // пропадала, как только приходил пустой ответ. Если бэкенд прислал total (а он
    // приходит везде, где есть счётчик «Showing N results»), сверяемся с ним и не
    // обещаем того, чего нет. Берём именно total этого ответа: applyTotal накапливает
    // total в сторе и не сбрасывает его между локациями, поэтому фолбэк на стор дал бы
    // count предыдущей выборки — список молча обрезался бы на её размере. Контракт
    // допускает count: null — тогда остаёмся на прежнем оптимистичном правиле и лишний
    // пустой запрос всё же случится.
    const total = response.total.read.count;

    // Хвост, оставленный при мердже, мог достаться от выборки, которая с тех пор
    // уменьшилась (кто-то удалил файлы). Если total известен — подрезаем, чтобы на экране
    // не висели заведомо лишние карточки.
    if (revalidating && total !== null && assets.length > total) {
      assets = assets.slice(0, total);
    }

    const hasNextPage = response.assets.length > 0 && (total === null || assets.length < total);

    dispatch(assetsChanged({ data: { ...response, assets }, hasNextPage, scrollHash }));

    // Сохраняем актуальный снапшот списка в кэш (берём уже смёрдженное состояние из стора,
    // чтобы корректно учесть подгруженные страницы и накопленный total).
    if (cachingEnabled) {
      // Размер страницы задаёт бэкенд, поэтому берём его из ответа на refresh (это и есть
      // первая страница), а при подгрузке — из уже записанного ранее значения. Персисту он
      // нужен, чтобы обрезать запись ровно по границе первой страницы, а не по угаданному числу.
      const pageSize =
        mode === LoadingMode.refresh
          ? response.assets.length
          : getCache!(getState())[cacheKey!]?.pageSize;

      dispatch(
        storeCache!({
          key: cacheKey!,
          entry: {
            data: getData(getState()),
            hasNextPage: getHasNextPage!(getState()),
            scrollHash: getScrollHash(getState()),
            pageSize,
          },
        }),
      );
    }
  } else {
    // При тихом ре-валидейте (данные из кэша уже на экране) ошибку не показываем:
    // ни деструктивной заменой на errorState, ни тостом — это сбивало бы с толку.
    if (revalidating) {
      return;
    }
    void message.error(errorMessage);
    if (mode === LoadingMode.refresh) {
      dispatch(
        assetsChanged({ data: errorState, hasNextPage: false, scrollHash: newScrollHash() }),
      );
    }
  }
}
